import React, { useEffect, useMemo, useState } from "react";
import { Search, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import DataTable from "react-data-table-component";
import { adminServices } from "../../services/AdminServices";

const LIMIT = 10;

const paginationStyles = {
  wrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    padding: "16px 0",
    gap: "8px",
    flexWrap: "wrap"
  },
  link: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    padding: "6px 8px"
  },
  linkDisabled: {
    color: "#9ca3af",
    cursor: "not-allowed",
    opacity: 0.7
  },
  pageNum: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    minWidth: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    border: "none",
    backgroundColor: "transparent",
    borderRadius: "6px"
  },
  pageNumActive: {
    backgroundColor: "#e5e7eb",
    color: "#374151",
    fontWeight: 500
  },
  ellipsis: {
    color: "#6b7280",
    fontSize: "0.875rem",
    padding: "0 4px",
    minWidth: "24px",
    textAlign: "center"
  }
};

function CustomPagination({ rowCount, rowsPerPage, currentPage, onChangePage, totalRows }) {
  const total = rowCount ?? totalRows ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    if (currentPage <= 4) {
      for (let i = 1; i <= Math.min(5, totalPages); i++) pages.push(i);
      if (totalPages > 5) pages.push("...");
      if (totalPages > 5) pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      if (totalPages > 5) pages.push("...");
      for (let i = Math.max(1, totalPages - 4); i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div style={paginationStyles.wrapper}>
      <button
        type="button"
        aria-label="Previous page"
        style={{
          ...paginationStyles.link,
          ...(!canPrev ? paginationStyles.linkDisabled : {})
        }}
        onClick={() => canPrev && onChangePage(currentPage - 1)}
        disabled={!canPrev}
      >
        <ChevronLeft size={18} />
        Previous
      </button>
      {pageNumbers.map((p, i) =>
        p === "..." ? (
          <span key={`ellipsis-${i}`} style={paginationStyles.ellipsis}>
            ...
          </span>
        ) : (
          <button
            key={p}
            type="button"
            style={{
              ...paginationStyles.pageNum,
              ...(p === currentPage ? paginationStyles.pageNumActive : {})
            }}
            onClick={() => onChangePage(p)}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        aria-label="Next page"
        style={{
          ...paginationStyles.link,
          ...(!canNext ? paginationStyles.linkDisabled : {})
        }}
        onClick={() => canNext && onChangePage(currentPage + 1)}
        disabled={!canNext}
      >
        Next
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

const AuditLogs = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [moduleFilter, setModuleFilter] = useState("all"); // all | item-master | order-management
  const [showModuleDropdown, setShowModuleDropdown] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchAuditLogs = async () => {
      setLoading(true);
      try {
        const res = await adminServices.getAuditLog({ page, limit: LIMIT });
        const history = res?.data?.history ?? res?.data;
        const raw = Array.isArray(history) ? history : (Array.isArray(res?.data) ? res.data : []);
        setTotalRecords(res?.data?.totalRecords ?? res?.data?.total ?? raw.length);
        const mapped = raw.map((e, idx) => {
          const dtRaw = e.dateTime || e.timestamp || e.date || "";
          const dt = dtRaw ? new Date(dtRaw) : null;
          const timestamp = dt && !Number.isNaN(dt.getTime()) ? dt.toLocaleString() : (dtRaw || "");

          const action = e.action || "";
          const table = e.table || "";
          const activity = action;

          const name = e.userName || "";
          const email = e.email || "";
          const source = e.source || "";

          const avatarName = name || email || source || "User";
          const avatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(avatarName)}&background=random`;

          return {
            id: e.auditLogId ?? e.id ?? idx + 1,
            timestamp,
            activity: activity || "-",
            table: table || "",
            user: {
              name: name || source || "-",
              email: email || "",
              avatar
            },
            details: e.description
          };
        });

        setAuditLogs(mapped);
      } catch (err) {
        console.error("Failed to fetch audit logs:", err);
        setAuditLogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLogs();
  }, [page, moduleFilter]);

  const filteredData = useMemo(() => {
    return auditLogs.filter((log) => {
      const q = searchQuery.toLowerCase().trim();
      if (q) {
        const matchesSearch =
          log.activity?.toLowerCase().includes(q) ||
          log.user?.name?.toLowerCase().includes(q) ||
          log.user?.email?.toLowerCase().includes(q) ||
          log.details?.toLowerCase().includes(q) ||
          log.timestamp?.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }

      if (moduleFilter === "item-master") {
        return (log.table || "").toLowerCase().includes("product");
      }
      if (moduleFilter === "order-management") {
        return (log.table || "").toLowerCase().includes("order");
      }
      return true;
    });
  }, [auditLogs, searchQuery, moduleFilter]);

  const columns = [
    {
      name: "Timestamp",
      selector: row => row.timestamp || "-",
      sortable: true,
      width: "25%"
    },
    {
      name: "Activity",
      selector: row => row.activity || "-",
      sortable: true,
      width: "25%"
    },
    {
      name: "User/Source",
      cell: row => {
        if (!row.user || !row.user.name) return <div>-</div>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <img
              src={row.user.avatar}
              alt={row.user.name}
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                objectFit: "cover"
              }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontFamily: "Figtree, sans-serif", fontSize: "0.875rem", fontWeight: 500, color: "#111827" }}>
                {row.user.name}
              </span>
              <span style={{ fontFamily: "Figtree, sans-serif", fontSize: "0.75rem", color: "#6b7280" }}>
                {row.user.email}
              </span>
            </div>
          </div>
        );
      },
      sortable: true,
      width: "25%"
    },
    {
      name: "Details",
      selector: row => row.details || "-",
      sortable: true,
      wrap: true,
      width: "25%"
    }
  ];

  const customStyles = {
    table: {
      style: {
        tableLayout: 'fixed'
      }
    },
    headRow: {
      style: {
        backgroundColor: "#0F2A40",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600,
        minHeight: "48px"
      }
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        textTransform: "uppercase",
        textAlign: "left"
      }
    },
    rows: {
      style: {
        minHeight: "56px"
      }
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px"
      }
    },
    pagination: {
      style: {
        justifyContent: "center",
        flexWrap: "wrap"
      }
    }
  };

  return (
    <div className="dashboard-content">
      <div className="audit-logs-page">
        <div className="audit-logs-header">
          <h1 className="dashboard-title">Audit Logs</h1>
        </div>

        {/* Search and Filter Section */}
        <div className="audit-logs-search-filter">
          <div className="audit-logs-search-container">
            <Search className="audit-logs-search-icon" size={18} />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="audit-logs-search-input"
            />
          </div>
          <div style={{ position: "relative" }}>
            <button
              className="audit-logs-filter-btn"
              type="button"
              onClick={() => setShowModuleDropdown((v) => !v)}
            >
              <Filter size={18} />
              {moduleFilter === "all"
                ? "Filters (All)"
                : moduleFilter === "item-master"
                ? "Filters (Item Master)"
                : "Filters (Order Management)"}
            </button>
            {showModuleDropdown && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "100%",
                  marginTop: 8,
                  backgroundColor: "#ffffff",
                  borderRadius: 8,
                  boxShadow: "0 10px 25px rgba(15, 23, 42, 0.15)",
                  border: "1px solid #e5e7eb",
                  minWidth: 220,
                  zIndex: 20
                }}
              >
                <div
                  style={{
                    padding: "10px 12px",
                    borderBottom: "1px solid #e5e7eb",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#6b7280",
                    textTransform: "uppercase"
                  }}
                >
                  Filter by Module
                </div>
                {[
                  { id: "all", label: "All" },
                  { id: "item-master", label: "Item Master" },
                  { id: "order-management", label: "Order Management" }
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      setModuleFilter(opt.id);
                      setPage(1);
                      setShowModuleDropdown(false);
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "8px 12px",
                      border: "none",
                      backgroundColor: moduleFilter === opt.id ? "#f3f4f6" : "#ffffff",
                      textAlign: "left",
                      fontSize: 13,
                      cursor: "pointer",
                      color: "#111827"
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Table Section */}
        <div className="audit-logs-table-section">
          <div className="audit-logs-table-container">
            <DataTable
              columns={columns}
              data={filteredData}
              customStyles={customStyles}
              striped
              highlightOnHover
              noHeader={false}
              pagination
              paginationServer
              paginationTotalRows={totalRecords}
              paginationPerPage={LIMIT}
              paginationDefaultPage={page}
              onChangePage={(newPage) => setPage(newPage)}
              paginationComponent={CustomPagination}
              paginationComponentOptions={{
                rowsPerPageText: "",
                rangeSeparatorText: "of",
                noRowsPerPage: true,
                selectAllRowsItem: false
              }}
              progressPending={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;

